import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { MediaModal, ModalSpinner } from "@/components/catalog/MediaModal";

const PLATFORM = { youtube: "YouTube", tiktok: "TikTok" };

function embedSrc(v) {
  if (v.platform === "tiktok") return `https://www.tiktok.com/embed/v2/${v.id}?autoplay=1`;
  return `https://www.youtube.com/embed/${v.id}?autoplay=1&playsinline=1&rel=0&modestbranding=1&origin=${encodeURIComponent(window.location.origin)}`;
}

function originalUrl(v) {
  if (v.url) return /^https?:\/\//.test(v.url) ? v.url : `https://${v.url}`;
  return v.platform === "tiktok" ? `https://www.tiktok.com/embed/v2/${v.id}` : `https://www.youtube.com/watch?v=${v.id}`;
}

function Player({ unitId, video }) {
  const [state, setState] = useState("checking");
  useEffect(() => {
    let alive = true;
    api.get(`/catalog/units/${unitId}/video-status`)
      .then(({ data }) => alive && setState(data.ok ? "loading" : "error"))
      .catch(() => alive && setState("loading"));
    const t = setTimeout(() => alive && setState((s) => (s === "ready" ? s : "error")), 20000);
    return () => { alive = false; clearTimeout(t); };
  }, [unitId]);

  if (state === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-[#1b1f1f] p-6 text-center text-white" data-testid="video-error">
        <p>No pudimos cargar el video</p>
        <a href={originalUrl(video)} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="video-open-platform">
          <ExternalLink size={16} /> Verlo en {PLATFORM[video.platform]}
        </a>
      </div>
    );
  }
  return (
    <div className="relative h-full w-full bg-black">
      {state !== "ready" && <div className="absolute inset-0 flex items-center justify-center"><ModalSpinner label="Cargando video…" testId="video-loading" /></div>}
      {state !== "checking" && (
        <iframe src={embedSrc(video)} title="Video de la unidad" className="absolute inset-0 h-full w-full" frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write" allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setState("ready")} data-testid="video-iframe" />
      )}
    </div>
  );
}

export function VideoModal({ open, onClose, unitId, video }) {
  const vertical = video.orientation === "vertical";
  const style = vertical
    ? { width: "min(94vw, calc(90vh * 9 / 16))", aspectRatio: "9 / 16", maxHeight: "90vh" }
    : { width: "min(94vw, 1100px, calc(85vh * 16 / 9))", aspectRatio: "16 / 9" };
  return (
    <MediaModal open={open} onClose={onClose} title={`Video en ${PLATFORM[video.platform]}`} testId="video-modal"
      contentClassName={`bg-black ${vertical ? "video-vertical" : "video-horizontal"}`} contentStyle={style}>
      {open && <Player unitId={unitId} video={video} />}
    </MediaModal>
  );
}
